import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    IconButton,
    TextField,
    Typography,
    Paper,
    Avatar,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SendIcon from "@material-ui/icons/Send";
import CloseIcon from "@material-ui/icons/Close";
import RefreshIcon from "@material-ui/icons/Refresh";
import DescriptionIcon from "@material-ui/icons/Description";
import { useHistory } from "react-router-dom";
import { sendChatMessage } from "../../pages/Services/SearchService";

const PPF_PRIMARY = "#117aa7";
const PPF_DARK    = "#2e4765";

const useStyles = makeStyles(() => ({
    fab: {
        position: "fixed",
        bottom: 28,
        right: 28,
        zIndex: 1300,
        width: 54,
        height: 54,
        borderRadius: "50%",
        background: `linear-gradient(135deg, ${PPF_PRIMARY}, ${PPF_DARK})`,
        color: "#fff",
        boxShadow: "0 4px 16px rgba(17,122,167,0.45)",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "transform 0.2s",
        "&:hover": { transform: "scale(1.08)" },
    },
    window: {
        position: "fixed",
        bottom: 94,
        right: 28,
        zIndex: 1300,
        width: 370,
        height: 510,
        display: "flex",
        flexDirection: "column",
        borderRadius: 14,
        overflow: "hidden",
        boxShadow: "0 8px 36px rgba(17,122,167,0.22)",
    },
    header: {
        background: `linear-gradient(135deg, ${PPF_PRIMARY}, ${PPF_DARK})`,
        color: "#fff",
        padding: "10px 14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    headerLeft: {
        display: "flex",
        alignItems: "center",
        gap: 10,
    },
    headerActions: {
        display: "flex",
        alignItems: "center",
        gap: 2,
    },
    avatar: {
        backgroundColor: "#fff",
        color: PPF_PRIMARY,
        width: 34,
        height: 34,
        fontSize: 14,
        fontWeight: "bold",
    },
    messages: {
        flex: 1,
        overflowY: "auto",
        padding: "12px 14px",
        backgroundColor: "#f4f7fc",
        display: "flex",
        flexDirection: "column",
        gap: 10,
    },
    bubble: {
        maxWidth: "82%",
        padding: "9px 13px",
        borderRadius: 12,
        fontSize: 13.5,
        lineHeight: 1.55,
        whiteSpace: "pre-wrap",
    },
    botBubble: {
        backgroundColor: "#fff",
        color: "#1a1a1a",
        borderTopLeftRadius: 2,
        alignSelf: "flex-start",
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    },
    userBubble: {
        background: `linear-gradient(135deg, ${PPF_PRIMARY}, ${PPF_DARK})`,
        color: "#fff",
        borderTopRightRadius: 2,
        alignSelf: "flex-end",
    },
    candidateCard: {
        backgroundColor: "#fff",
        borderLeft: `3px solid ${PPF_PRIMARY}`,
        borderRadius: 8,
        padding: "8px 12px",
        marginTop: 5,
        boxShadow: "0 1px 4px rgba(17,122,167,0.10)",
        cursor: "pointer",
        transition: "background-color 0.15s",
        "&:hover": {
            backgroundColor: "#eef6fb",
            borderLeft: `3px solid ${PPF_DARK}`,
        },
    },
    candidateCardNoLink: {
        backgroundColor: "#fff",
        borderLeft: `3px solid ${PPF_PRIMARY}`,
        borderRadius: 8,
        padding: "8px 12px",
        marginTop: 5,
        boxShadow: "0 1px 4px rgba(17,122,167,0.10)",
    },
    candidateCardHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    candidateName: {
        fontWeight: 700,
        color: PPF_DARK,
        fontSize: 13.5,
        marginBottom: 2,
    },
    profileIcon: {
        color: PPF_PRIMARY,
        padding: 3,
        "&:hover": { color: PPF_DARK },
    },
    candidateDetail: {
        color: "#666",
        fontSize: 12,
        marginTop: 2,
    },
    showMore: {
        fontSize: 11.5,
        color: PPF_PRIMARY,
        marginTop: 5,
        paddingLeft: 4,
        cursor: "pointer",
        fontWeight: 600,
        "&:hover": { textDecoration: "underline" },
    },
    inputRow: {
        display: "flex",
        alignItems: "center",
        padding: "8px 10px",
        backgroundColor: "#fff",
        borderTop: "1px solid #e4ecf2",
        gap: 6,
    },
    input: {
        flex: 1,
        "& .MuiOutlinedInput-root": {
            borderRadius: 20,
            fontSize: 13.5,
            "& fieldset": { borderColor: "#d0dde8" },
            "&:hover fieldset": { borderColor: PPF_PRIMARY },
            "&.Mui-focused fieldset": { borderColor: PPF_PRIMARY },
        },
    },
    sendBtn: {
        color: PPF_PRIMARY,
        "&:disabled": { color: "#c0d4e0" },
    },
    dot: {
        display: "inline-block",
        width: 7,
        height: 7,
        borderRadius: "50%",
        backgroundColor: PPF_PRIMARY,
        margin: "0 2px",
        animation: "$bounce 1.2s infinite",
        "&:nth-child(2)": { animationDelay: "0.2s" },
        "&:nth-child(3)": { animationDelay: "0.4s" },
    },
    "@keyframes bounce": {
        "0%, 80%, 100%": { transform: "translateY(0)" },
        "40%": { transform: "translateY(-6px)" },
    },
}));

const renderBotText = (text) => {
    const html = text
        .replace(/Go\s*&\s*G[eé]v|Go\s*&\s*Dev/gi, "PeopleFine")
        .replace(/\bGia\b/gi, "Pia")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
        .replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, "<em>$1</em>")
        .replace(/\*+/g, "");
    return { __html: html };
};

const WELCOME = {
    role: "bot",
    type: "text",
    text: "Bonjour ! Je suis Pia, votre assistante RH\nComment puis-je vous aider ?",
};

const deduplicateCandidates = (candidates) => {
    const seen = new Set();
    return candidates.filter((c) => {
        const p = c.properties || c;
        const email = (p.email || "").toLowerCase().trim();
        const name  = (p.full_name || "").toLowerCase().trim();
        const key   = email && email !== "not found" ? email : name;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

export default function ChatBot() {
    const classes      = useStyles();
    const history      = useHistory();
    const [open, setOpen]                   = useState(false);
    const [messages, setMessages]           = useState([WELCOME]);
    const [input, setInput]                 = useState("");
    const [loading, setLoading]             = useState(false);
    const [conversationId, setConversationId] = useState(() => Date.now().toString());
    const bottomRef = useRef(null);

    useEffect(() => {
        if (open && bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages, open]);

    const handleNewConversation = () => {
        setMessages([WELCOME]);
        setConversationId(Date.now().toString());
    };

    const handleSend = async () => {
        const text = input.trim();
        if (!text || loading) return;

        setInput("");
        setMessages((prev) => [...prev, { role: "user", type: "text", text }]);
        setLoading(true);

        try {
            const { data } = await sendChatMessage(text, conversationId);

            if (data.type === "candidates" && Array.isArray(data.Candidates)) {
                const deduped = deduplicateCandidates(data.Candidates);
                setMessages((prev) => [
                    ...prev,
                    { role: "bot", type: "text", text: data.message || "Voici les profils correspondants :" },
                    { role: "bot", type: "candidates", candidates: deduped, expanded: false },
                ]);
            } else {
                setMessages((prev) => [
                    ...prev,
                    { role: "bot", type: "text", text: data.message || "Je suis là pour vous aider." },
                ]);
            }
        } catch {
            setMessages((prev) => [
                ...prev,
                { role: "bot", type: "text", text: "Une erreur est survenue. Veuillez réessayer." },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const toggleExpanded = (msgIndex) => {
        setMessages((prev) =>
            prev.map((m, i) => (i === msgIndex ? { ...m, expanded: !m.expanded } : m))
        );
    };

    const handleCandidateClick = (candidateUuid) => {
        if (!candidateUuid) return;
        setOpen(false);
        history.push(`/candidats/${candidateUuid}/view`);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <>
            {open && (
                <Paper className={classes.window} elevation={0}>
                    {/* Header */}
                    <Box className={classes.header}>
                        <Box className={classes.headerLeft}>
                            <Avatar className={classes.avatar}>P</Avatar>
                            <Box>
                                <Typography style={{ fontWeight: 700, fontSize: 14, lineHeight: 1 }}>Pia</Typography>
                                <Typography style={{ fontSize: 11, opacity: 0.8 }}>Assistante RH · En ligne</Typography>
                            </Box>
                        </Box>
                        <Box className={classes.headerActions}>
                            <IconButton
                                size="small"
                                onClick={handleNewConversation}
                                style={{ color: "#fff", opacity: 0.8 }}
                                title="Nouvelle conversation"
                            >
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => setOpen(false)} style={{ color: "#fff" }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Messages */}
                    <Box className={classes.messages}>
                        {messages.map((msg, i) => {
                            if (msg.type === "candidates") {
                                const visible   = msg.expanded ? msg.candidates : msg.candidates.slice(0, 5);
                                const remaining = msg.candidates.length - 5;
                                return (
                                    <Box key={i} style={{ alignSelf: "flex-start", width: "92%" }}>
                                        {visible.map((c, j) => {
                                            const p     = c.properties || c;
                                            const roles = p.roles_held;
                                            const hasLink = !!c.candidat_uuid;
                                            return (
                                                <Box
                                                    key={j}
                                                    className={classes.candidateCardNoLink}
                                                >
                                                    <Box className={classes.candidateCardHeader}>
                                                        <Typography className={classes.candidateName}>
                                                            {p.full_name || "—"}
                                                        </Typography>
                                                        {hasLink && (
                                                            <IconButton
                                                                size="small"
                                                                className={classes.profileIcon}
                                                                onClick={() => handleCandidateClick(c.candidat_uuid)}
                                                                title="Voir le profil"
                                                            >
                                                                <DescriptionIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                    {roles?.length > 0 && (
                                                        <Typography className={classes.candidateDetail}>
                                                            {Array.isArray(roles) ? roles[0] : roles}
                                                        </Typography>
                                                    )}
                                                    {p.location && p.location !== "not found" && (
                                                        <Typography className={classes.candidateDetail}>
                                                            {p.location}
                                                        </Typography>
                                                    )}
                                                    {p.years_of_experience != null && (
                                                        <Typography className={classes.candidateDetail}>
                                                            {p.years_of_experience} ans d'exp.
                                                        </Typography>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                        {!msg.expanded && remaining > 0 && (
                                            <Typography
                                                className={classes.showMore}
                                                onClick={() => toggleExpanded(i)}
                                            >
                                                +{remaining} autres profils trouvés — voir tous
                                            </Typography>
                                        )}
                                        {msg.expanded && (
                                            <Typography
                                                className={classes.showMore}
                                                onClick={() => toggleExpanded(i)}
                                            >
                                                Afficher moins
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            }
                            return (
                                <Box
                                    key={i}
                                    className={`${classes.bubble} ${msg.role === "bot" ? classes.botBubble : classes.userBubble}`}
                                >
                                    {msg.role === "bot"
                                        ? <span dangerouslySetInnerHTML={renderBotText(msg.text)} />
                                        : msg.text}
                                </Box>
                            );
                        })}

                        {loading && (
                            <Box className={`${classes.bubble} ${classes.botBubble}`} style={{ padding: "10px 14px" }}>
                                <span className={classes.dot} />
                                <span className={classes.dot} />
                                <span className={classes.dot} />
                            </Box>
                        )}
                        <div ref={bottomRef} />
                    </Box>

                    {/* Input */}
                    <Box className={classes.inputRow}>
                        <TextField
                            className={classes.input}
                            variant="outlined"
                            size="small"
                            placeholder="Écrivez votre message..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            multiline
                            rowsMax={3}
                            disabled={loading}
                        />
                        <IconButton
                            className={classes.sendBtn}
                            onClick={handleSend}
                            disabled={!input.trim() || loading}
                            size="small"
                        >
                            <SendIcon />
                        </IconButton>
                    </Box>
                </Paper>
            )}

            {/* Bouton flottant */}
            <Box className={classes.fab} onClick={() => setOpen((o) => !o)}>
                <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor">
                    <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h2a5 5 0 0 1 5 5v2a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5v-2a5 5 0 0 1 5-5h2V5.73A2 2 0 0 1 10 4a2 2 0 0 1 2-2zM9 9a3 3 0 0 0-3 3v2a3 3 0 0 0 3 3h6a3 3 0 0 0 3-3v-2a3 3 0 0 0-3-3H9zm1 3a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2zM7 8a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm10 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                </svg>
            </Box>
        </>
    );
}
